import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Icon, SystemProps } from "@storyofams/react-ui";
import { createClient } from "@supabase/supabase-js";
import { AnimatePresence, motion } from "framer-motion";
import { includes } from "lodash";
import { useIntl, defineMessages } from "react-intl";
import { useQuery } from "react-query";
import { ArrowLeft, ArrowRight } from "~components/common/Icons";
import { Text } from "~components/common/Text";
import { ProductByHandleQuery } from "~lib/shopify/sdk";
import { Description } from "./Description";
import { Instructions, Title } from "./Instructions";
import { Recipes } from "./Recipes";
import { Specifications } from "./Specifications";
import { Tabs } from "./Tabs";

const messages = defineMessages({
  questions: "vragen",
  aboutThisPlantType: "Over deze plantsoort",
  alsoAvailableInBox: "Ook beschikbaar in onze DIY box!",
  description: "Beschrijving",
  instructions: "Gebruik",
  specifications: "Ingrediënten",
  recepten: "Recepten",
});

export const Animate = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    exit={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ ease: "easeInOut", duration: 0.24 }}
  >
    {children}
  </motion.div>
);

export const InformationTabs = memo(
  ({
    product,
    isDescriptionDisabled = false,
    isIngredientsDisabled = false,
    ...props
  }: {
    product: ProductByHandleQuery["productByHandle"];
    isDescriptionDisabled?: boolean;
    isIngredientsDisabled?: boolean;
  } & SystemProps) => {
    const intl = useIntl();

    const [tab, setTab] = useState(() =>
      !isDescriptionDisabled ? "description" : "instructions"
    );
    const [open, setIsOpen] = useState(false);
    const [sorting, setSorting] = useState({
      label: "Populariteit",
      value: "rating",
      asc: true,
    });
    const setSortingMemo = useCallback((sort) => setSorting(sort), []);
    const getValue = (key) =>
      product?.metafields?.find((item) => item?.key === key)?.value;

    const ingredients = getValue("ingredienten_nieuw");
    const inci = getValue("inci_nieuw");

    const hasSpecifications =
      !!ingredients ||
      !!inci ||
      includes(
        ["Cosmetica", "Grondstof", "Merk", "Pakket"],
        getValue("wettelijk_type")
      );

    const hasInstructions =
      !!getValue("waarschuwingen") || getValue("gebruiksaanwijzing");
    const supabase = createClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL,
      process.env.NEXT_PUBLIC_SUPABASE_KEY
    );
    const ids = useMemo(
      () => product.variants.edges.map(({ node }) => node.id),
      [product]
    );
    const { data: recipes, isLoading } = useQuery(
      ["productRecipes", product.id, sorting],
      async () => {
        try {
          let { data } = await supabase
            .from("recipes_ingredients")
            .select("recipe_id")
            .in("shopify_variant_id", ids);
          if (data.length) {
            //@ts-ignore
            let query = supabase
              .rpc("search_recipes_new", {
                search_term: "",
              })
              .in(
                "id",
                data.map(({ recipe_id }) => recipe_id)
              )
              .eq("published", true)
              .order("display_order", { ascending: true })
              .order("published_at", { ascending: false });
            const { data: recipesData } = await query;
            return recipesData as any;
          }
          return [];
        } catch (error) {
          console.log(error);
        }
      },
      {
        refetchOnWindowFocus: false,
      }
    );
    const tabs = [
      !isDescriptionDisabled ? "description" : undefined,
      hasInstructions ? "instructions" : undefined,
      !isIngredientsDisabled && hasSpecifications
        ? "specifications"
        : undefined,
      recipes && recipes.length ? "recepten" : undefined,
    ];
    useEffect(() => {
      const filteredTabs = tabs.filter((item) => item);
      if (filteredTabs.length === 1) setTab(filteredTabs[0]);
    }, [tabs]);
    if (!product?.descriptionHtml && !hasInstructions && !hasSpecifications) {
      return <Box />;
    }
    return (
      <>
        <Box {...props} display={["none", "block"]}>
          <Tabs
            tabs={tabs
              .filter((tab) => !!tab)
              .map((msgId) => ({
                label: intl.formatMessage(messages[msgId]),
                tab: msgId,
              }))}
            activeTab={tab}
            isPP
            onClick={setTab}
            mb={[4, 6]}
          />
          <AnimatePresence>
            {tab === "description" && (
              <Animate>
                <Description
                  description={product?.descriptionHtml}
                  product={product}
                  // @ts-ignore
                  image={product?.descriptionImage?.value}
                  getValue={getValue}
                />
              </Animate>
            )}

            {tab === "instructions" && hasInstructions && (
              <Animate>
                <Instructions getValue={getValue} />
              </Animate>
            )}

            {tab === "specifications" &&
              hasSpecifications &&
              !isIngredientsDisabled && (
                <Animate>
                  <Specifications getValue={getValue} product={product} />
                </Animate>
              )}
            {tab === "recepten" && recipes && !isLoading && (
              <Animate>
                <Recipes
                  recipes={recipes}
                  sorting={sorting}
                  setSortingMemo={setSortingMemo}
                />
              </Animate>
            )}
          </AnimatePresence>
        </Box>
        <Box display={["flex", "none"]} flexDirection="column" m={0}>
          {tabs
            .filter((tab) => !!tab)
            .map((msgId, i) => (
              <Box
                onClick={() => {
                  setTab(msgId);
                  setIsOpen(true);
                }}
                mb={2}
                display="flex"
                height="54px"
                alignItems="center"
                justifyContent="space-between"
                key={msgId}
                borderTop="1px solid grey200"
                borderBottom="1px solid grey200"
              >
                <Text fontSize={18} fontWeight={500}>
                  {intl.formatMessage(messages[msgId])}
                </Text>
                <Icon icon={ArrowRight} fontSize={2}></Icon>
              </Box>
            ))}
        </Box>

        <Box
          display={open ? "block" : "none"}
          position="fixed"
          top="-32px"
          left="0"
          backgroundColor="rgba(0,0,0,0.4)"
          width="100%"
          height="102%"
          zIndex={10000}
          onClick={() => setIsOpen(false)}
        >
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ x: -300 }}
                animate={{ x: 0 }}
                transition={{ duration: 0.2 }}
                style={{ height: "100%", width: "85%" }}
              >
                <Box
                  backgroundColor="white"
                  borderBottom="1px solid grey400"
                  display="flex"
                  pt={2}
                  pl={2}
                  onClick={() => setIsOpen(false)}
                >
                  <Icon
                    mt={"2px"}
                    icon={ArrowLeft}
                    fontSize={3}
                    mr={1}
                    pb={2}
                  />
                  <Title>Terug naar product</Title>
                </Box>
                <Box
                  backgroundColor="white"
                  px={2}
                  height="93%"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Box overflow="auto" height="100%">
                    {tab === "description" && (
                      <Description
                        description={product?.descriptionHtml}
                        product={product}
                        // @ts-ignore
                        image={product?.descriptionImage?.value}
                        getValue={getValue}
                      />
                    )}
                    {tab === "instructions" && hasInstructions && (
                      <Instructions getValue={getValue} />
                    )}
                    {tab === "specifications" && hasSpecifications && (
                      <Specifications getValue={getValue} product={product} />
                    )}
                    {tab === "recepten" && !isLoading && recipes && (
                      <Recipes
                        recipes={recipes}
                        sorting={sorting}
                        setSortingMemo={setSortingMemo}
                      />
                    )}
                    <Box height="20px" />
                  </Box>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </>
    );
  }
);
